import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  message,
  Flex,
  Typography,
} from "antd";
import userManagementService from "../../services/userManagementService";

const { Option } = Select;
const { Title } = Typography;

const UserCreateForm = ({
  visible,
  onCancel,
  onSuccess,
  locations,
  setLocations,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(2);
  const [successMessage, setSuccessMessage] = useState("");

  const getPermissionLabel = (type) => {
    switch (type) {
      case 2: return "Expert";
      case 4: return "Team Lead";
      case 6: return "Inhouse Manager";
      case 8: return "Quality Manager";
      case 10: return "Business Development Executive";
      case 1: return "Business Development Trainee";
      case 9: return "Lead Generation Executive";
      case 7: return "Telecaller";
      default: return "Expert";
    }
  };  

  const getSuffix = (type) => {
    switch (type) {
      case 4:
        return "_tl";
      case 6:
        return "_mgr";
      case 8:
        return "_qm";
      case 10:
        return "_bde";
      case 1:
        return "_bdt";
      case 9:
        return "_lge";
      case 7:
        return "_tc";
      default:
        return "_exp";
    }
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSuccessMessage("");
      setUserType(2);
    }
  }, [visible, form]);

  useEffect(() => {
    if (visible) {
      userManagementService.getLocationsDropdown().then((res) => {
        if (res.success) {
          setLocations(res.data);
        } else {
          message.error("Failed to load locations");
        }
      });
    }
  }, [visible]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const suffix = getSuffix(userType);
      const payload = {
        ...values,
        username: values.username + suffix,
        locations: values.locations,
        user_type: Number(values.user_type),
      };
      console.log(payload);
      const res = await userManagementService.createUser(payload);
      setLoading(false);
      if (res.success) {
        setSuccessMessage(res.data);
        form.resetFields();
      } else {
        message.error(res.error || "Failed to create user");
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccessMessage("");
    onSuccess && onSuccess();
    onCancel && onCancel();
  };

  return (
    <Modal
      visible={visible}
      className="custom-modal"
      title={
        successMessage ? (
          <Flex justify="space-between" className="custom-modal-header">
            <Title
              level={3}
              style={{ color: "white", margin: 0, marginLeft: 10 }}>
              Account Setup Complete
            </Title>
          </Flex>
        ) : (
          <Flex justify="space-between" className="custom-modal-header">
            <Title
              level={3}
              style={{ color: "white", margin: 0, marginLeft: 10 }}>
              Create User
            </Title>
          </Flex>
        )
      }
      onCancel={handleClose}
      confirmLoading={loading && !successMessage}
      okText={successMessage ? null : "Create"}
      footer={null}>
      {successMessage ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
          }}>
          <Typography.Title level={5}>Your Password is:</Typography.Title>
          <Typography.Text
            style={{ fontWeight: "bold", fontSize: 22 }}
            type="success">
            {successMessage}
          </Typography.Text>
        </div>
      ) : (
        <Form
          style={{ width: "100%" }}
          form={form}
          layout="vertical"
          initialValues={{
            user_type: 2,
            group_permissions: getPermissionLabel(2),
            locations: [],
          }}
          onFinish={handleOk}>
          <Flex
            gap={12}
            style={{ flexWrap: "wrap", width: "100%" }}>
            <Col style={{ width: "100%" }} xs={24} sm={24} md={11}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[
                  { required: true, message: "Please input first name" },
                ]}>
                <Input placeholder="Enter first name"/>
              </Form.Item>
            </Col>
            <Col style={{ width: "100%" }} xs={24} sm={24} md={11}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: "Please input last name" }]}>
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Flex>

          <Flex
            gap={12}
            style={{ flexWrap: "wrap", width: "100%" }}>
            <Col xs={24} md={11} style={{ width: "100%" }}>
              <Form.Item
                label="User Type"
                name="user_type"
                rules={[
                  { required: true, message: "Please select user type" },
                ]}>
                <Select
                  value={userType}
                  onChange={(value) => {
                    setUserType(value);
                    form.setFieldsValue({
                      user_type: value,
                      group_permissions: getPermissionLabel(value),
                    });
                  }}
                  options={[
                    { label: "Expert", value: 2 },
                    { label: "Team Lead", value: 4 },
                    { label: "Inhouse Manager", value: 6 },
                    { label: "Quality Manager", value: 8 },
                    { label: "Business Development Executive", value: 10 },
                    { label: "Business Development Trainee", value: 1 },
                    { label: "Lead Generation Executive", value: 9 },
                    { label: "Telecaller", value: 7 },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={11} style={{ width: "100%" }}>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: "Please input username" }]}>
                <Input addonAfter={<span>{getSuffix(userType)}</span>} />
              </Form.Item>
            </Col>
          </Flex>

          <Flex
            gap={12}
            style={{ flexWrap: "wrap", width: "100%" }}>
            <Col xs={24} md={11} style={{ width: "100%" }}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please input email" },
                  { type: "email", message: "Invalid email" },
                ]}>
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>

            <Col xs={24} md={11} style={{ width: "100%" }}>
              <Form.Item
                label="Location"
                name="locations"
                rules={[
                  {
                    required: true,
                    type: "array",
                    message: "Please select at least one location",
                  },
                ]}>
                <Select
                  mode="multiple"
                  placeholder="Select location"
                  allowClear>
                  {locations.map((loc) => (
                    <Option key={loc.uid} value={loc.uid}>
                      {loc.location}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Flex>

          <Col xs={24} style={{ width: "100%" }}>
            <Form.Item label="Group Permissions" name="group_permissions">
              <Input disabled />
            </Form.Item>
          </Col>

          <Form.Item style={{ marginTop: 24, marginBottom: 0, width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading && !successMessage}>
                Create
              </Button>
            </div>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UserCreateForm;
